import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ShieldCheck, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const FINANCIAL = new Set([
  "financial_records", "revenue", "doctor_earnings", "technician_earnings",
  "expenses", "cash", "banks", "budgets", "profitability", "discounts_refunds",
]);
const LAB_ROLES = new Set(["laboratory_coordinator", "laboratory_technician"]);

export default function PermissionCenter() {
  const t = useT();
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState("");
  const [perm, setPerm] = useState(null);
  const [working, setWorking] = useState({});
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const selectedUser = users.find((u) => u.id === selected);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role !== "owner")));
    api.get("/meta/permissions-catalog").then((r) => setCatalog(r.data));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setPerm(null);
    api.get(`/users/${selected}/permissions`).then((r) => {
      setPerm(r.data);
      setWorking({ ...r.data.effective });
      setDefaults(r.data.defaults);
    });
  }, [selected]);

  const toggle = (key) => setWorking((w) => ({ ...w, [key]: !w[key] }));

  const save = async () => {
    const overrides = {};
    Object.keys(working).forEach((k) => {
      if (working[k] !== defaults[k]) overrides[k] = working[k];
    });
    setSaving(true);
    try {
      await api.put(`/users/${selected}/permissions`, { overrides });
      toast.success(t("permissions.permissionsUpdated"));
      const r = await api.get(`/users/${selected}/permissions`);
      setPerm(r.data);
      setWorking({ ...r.data.effective });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const isLocked = (resource) =>
    selectedUser && LAB_ROLES.has(selectedUser.role) && FINANCIAL.has(resource);

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]">
      <PageHeader title={t("permissions.title")} subtitle={t("permissions.subtitle")}>
        {selected && (
          <Button onClick={save} data-testid="save-permissions-button" disabled={saving} className="bg-navy hover:bg-navy-soft text-white gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("common.saveChanges")}
          </Button>
        )}
      </PageHeader>

      <div className="max-w-sm mb-6">
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger data-testid="select-employee-permissions"><SelectValue placeholder={t("permissions.selectEmployee")} /></SelectTrigger>
          <SelectContent>
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.full_name} · {t(`roles.${u.role}`, ROLE_LABELS[u.role] || u.role)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selected && !perm && <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>}

      {perm && catalog && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-100 bg-slate-50">
            <ShieldCheck className="h-4 w-4 text-navy" />
            <span className="text-sm text-slate-600">
              {t("permissions.editing")} <strong className="text-navy">{selectedUser?.full_name}</strong>{t("permissions.labBlocked")}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left font-semibold text-slate-500 px-6 py-3">{t("permissions.resource")}</th>
                  {catalog.actions.map((a) => (
                    <th key={a} className="text-center font-semibold text-slate-500 px-3 py-3">{t(`permissions.a_${a}`, a)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {catalog.resources.map((res) => (
                  <tr key={res.key} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-6 py-2.5">
                      <span className="font-medium text-slate-800">{res.label}</span>
                      {FINANCIAL.has(res.key) && <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-600">{t("permissions.financial")}</span>}
                    </td>
                    {catalog.actions.map((a) => {
                      const key = `${res.key}:${a}`;
                      const locked = isLocked(res.key);
                      return (
                        <td key={a} className="text-center px-3 py-2.5">
                          {locked ? (
                            <Lock className="h-4 w-4 text-slate-300 mx-auto" />
                          ) : (
                            <Checkbox
                              data-testid={`perm-${key}`}
                              checked={!!working[key]}
                              onCheckedChange={() => toggle(key)}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
